import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Dimensions, Pressable, Icon3 } from '../lib/react-native-compat';
import { useAtom, useAtomValue } from 'jotai';
import {
    selectedJobAtom,
    selectedVersionAtom,
    selectedTeamAtom,
    dataSourceJobsAtom,
    dataSourceVersionAtom,
    jobUserDataAtom,
    dashboardTeamUserDataAtom,
    dashboardCostDetailDataAtom,
    dashboardOpenCommitmentsDataAtom,
    dashboardBillingsDataAtom,
    dashboardEarnedRevenueDataAtom,
    dashboardLaborHoursByWeekDataAtom,
    dashboardCacheKeyAtom
} from '../store/company';
import {
    loadTeamUserDataMain,
    loadTeamBurnToolData,
    loadTeamCostDetail,
    loadTeamOpemCommitments,
    loadTeamCostDetailProjections,
    loadTeamBillings,
    loadCostDetailBillings,
    loadFTMSTickets,
    loadFTMSInvoices,
    loadTeamLaborHoursByWeek,
    fNum,
    pNum
} from '../db/company';
import { Line, Bar } from 'react-chartjs-2';
import { SERIES, alpha } from '../styles/chartColors';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import moment from 'moment';
import { MdClose } from 'react-icons/md';
import TeamGantt from './teamGantt';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const jobs = useAtomValue(dataSourceJobsAtom);
    const versions = useAtomValue(dataSourceVersionAtom);
    const jobUserData = useAtomValue(jobUserDataAtom);
    const [isLoading, setIsLoading] = useState(true);
    const [dashView, setDashView] = useState('Overview');
    
    // Dashboard cache atoms
    const [cachedTeamUserData, setCachedTeamUserData] = useAtom(dashboardTeamUserDataAtom);
    const [cachedCostDetailData, setCachedCostDetailData] = useAtom(dashboardCostDetailDataAtom);
    const [cachedOpenCommitmentsData, setCachedOpenCommitmentsData] = useAtom(dashboardOpenCommitmentsDataAtom);
    const [cachedBillingsData, setCachedBillingsData] = useAtom(dashboardBillingsDataAtom);
    const [cachedEarnedRevenueData, setCachedEarnedRevenueData] = useAtom(dashboardEarnedRevenueDataAtom);
    const [cachedLaborHoursByWeekData, setCachedLaborHoursByWeekData] = useAtom(dashboardLaborHoursByWeekDataAtom);
    const [cacheKey, setCacheKey] = useAtom(dashboardCacheKeyAtom);
    const [expandedMetric, setExpandedMetric] = useState(null);
    const [jobsDueForBillingDetails, setJobsDueForBillingDetails] = useState([]);
    const [activeProjectsDetails, setActiveProjectsDetails] = useState([]);
    const [jobsFinishingDetails, setJobsFinishingDetails] = useState([]);
    const [jobsStartingDetails, setJobsStartingDetails] = useState([]);
    const [newJobsDetails, setNewJobsDetails] = useState([]);
    const [totalBacklogDetails, setTotalBacklogDetails] = useState([]);
    const [overUnderBillingDetails, setOverUnderBillingDetails] = useState([]);
    const [versionInfo, setVersionInfo] = useState({})
    // Dashboard metrics state
    const [metrics, setMetrics] = useState({
        totalBacklog: 0,
        activeProjects: { active: 0, pending: 0 },
        overUnderBilling: 0,
        jobsDueForBill: 0,
        jobsFinishing: 0,
        jobsStartingNext4Weeks: 0,
        newJobsLast30Days: 0
    });

    // Chart data state
    const [chartData, setChartData] = useState({
        laborHoursTrend: {
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
            datasets: [{
                label: 'Labor Hours',
                data: [20, 30, 40, 50, 45, 60],
                borderColor: SERIES[0],
                backgroundColor: alpha(SERIES[0], 0.1),
                tension: 0.4,
                borderWidth: 3,
                pointRadius: 4,
                pointBackgroundColor: SERIES[0]
            }]
        },
        jobsFinishingChart: {
            labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
            datasets: [{
                label: 'Jobs',
                data: [1, 3, 2, 1, 4],
                backgroundColor: SERIES[0]
            }]
        }
    });

    // Job-level KPIs state
    const [jobKPIs, setJobKPIs] = useState([
        {
            jobName: 'Job 1',
            forecast: 3250000,
            variance: -100000,
            gm: 15.6,
            cpi: 1.02,
            spi: 1.12,
            complete: 45,
            billingStatus: 'Over Billed',
            endDate: -15,
            riskCount: 4,
            hrs: 200
        },
        // Additional jobs will be populated from actual data
    ]);

    const [contingencyMetrics, setContingencyMetrics] = useState([
        { label: 'Total Contingency', value: '$370k' },
        { label: 'Total Active Projects GM', value: '8.2M' },
        { label: 'Total Active Projects GM', value: '8.2M' },
        { label: 'Total Active Projects GM', value: '8.2M' },
        { label: 'Total Active Projects GM', value: '8.2M' },
        { label: 'Total Active Projects GM', value: '8.2M' }
    ]);

    useEffect(() => {
        if(versions && versions.length > 0) {
           console.log('version info', versions.find(v => v.Version_GUID === selectedVersion))
        }
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion))
    }, [versions, selectedVersion]);

    useEffect(() => {
        if (selectedTeam && selectedVersion && jobs && jobs.length > 0 && versionInfo?.Version_Start_DateTime) {
            loadDashboardData();
        }
    }, [selectedTeam, selectedVersion, jobs, versionInfo]);

    const loadDashboardData = async () => {
        try {
            setIsLoading(true);
            const teamJobs = Array.isArray(jobs) ? jobs.filter(job =>
                job.Cat_Code_BU_27 === selectedTeam
            ) : [];

            const jobIds = teamJobs.map(job => job.BU_ID);
            const nonTMJobIds = teamJobs.filter(job => job.Cat_Code_BU_08 !== 'T&M').map(job => job.BU_ID);

            if (jobIds.length === 0) {
                setIsLoading(false);
                return;
            }

            // Make sure versionInfo is set before calculating metrics
            if (!versionInfo?.Version_Start_DateTime) {
                console.error('Version info not loaded yet');
                setIsLoading(false);
                return;
            }

            // Check if we have cached data for this team+version combination
            const currentCacheKey = `${selectedTeam}_${selectedVersion}`;
            const hasCachedData = cacheKey === currentCacheKey && 
                cachedTeamUserData && 
                cachedCostDetailData && 
                cachedOpenCommitmentsData && 
                cachedBillingsData && 
                cachedEarnedRevenueData && 
                cachedLaborHoursByWeekData;

            let teamData, costDetailData, openCommitmentsData, billingsData, earnedRevenueData, laborHoursByWeekData;

            if (hasCachedData) {
                console.log('Using cached dashboard data for', currentCacheKey);
                teamData = cachedTeamUserData;
                costDetailData = cachedCostDetailData;
                openCommitmentsData = cachedOpenCommitmentsData;
                billingsData = cachedBillingsData;
                earnedRevenueData = cachedEarnedRevenueData;
                laborHoursByWeekData = cachedLaborHoursByWeekData;
            } else {
                console.log('Loading fresh dashboard data for', currentCacheKey);
                [teamData, costDetailData, openCommitmentsData, billingsData, earnedRevenueData, laborHoursByWeekData] = await Promise.all([
                    loadTeamUserDataMain({
                        ids: jobIds,
                        forecastGuid: selectedVersion
                    }),
                    // loadTeamBurnToolData({
                    //     ids: jobIds,
                    //     forecastGuid: selectedVersion
                    // }),
                    loadTeamCostDetail({
                        ids: jobIds,
                        forecastGuid: selectedVersion,
                        wip: moment().add(1, 'month').format('YYYY-MM-DD')
                    }),
                    loadTeamOpemCommitments({
                        ids: jobIds,
                        forecastGuid: selectedVersion
                    }),
                    loadTeamBillings({
                        ids: jobIds,
                        forecastGuid: selectedVersion
                    }),
                    loadCostDetailBillings({
                        ids: jobIds,
                        version: selectedVersion
                    }),
                    loadTeamLaborHoursByWeek({
                        ids: nonTMJobIds,
                        forecastGuid: selectedVersion,
                        wip: moment().format('YYYY-MM-DD')
                    })
                ]);
                
                // Deduplicate teamData before caching
                const deduplicatedTeamData = teamData?.data ? {
                    ...teamData,
                    data: (() => {
                        const grouped = {};
                        teamData.data.forEach(item => {
                            const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Row_ID}_${item.Forecast_Field_Name}`;
                            if (!grouped[key]) {
                                grouped[key] = [];
                            }
                            grouped[key].push(item);
                        });

                        return Object.values(grouped).map(group => {
                            if (group.length === 1) return group[0];
                            
                            // Sort by DSS_Updated_Datetime first, then DSS_Created_Date, keep most recent
                            return group.reduce((mostRecent, current) => {
                                const currentUpdated = current.DSS_Updated_Datetime ? new Date(current.DSS_Updated_Datetime) : null;
                                const currentCreated = current.DSS_Created_Date ? new Date(current.DSS_Created_Date) : null;
                                const mostRecentUpdated = mostRecent.DSS_Updated_Datetime ? new Date(mostRecent.DSS_Updated_Datetime) : null;
                                const mostRecentCreated = mostRecent.DSS_Created_Date ? new Date(mostRecent.DSS_Created_Date) : null;

                                // Compare Updated dates first
                                if (currentUpdated && mostRecentUpdated) {
                                    return currentUpdated > mostRecentUpdated ? current : mostRecent;
                                }
                                if (currentUpdated && !mostRecentUpdated) return current;
                                if (!currentUpdated && mostRecentUpdated) return mostRecent;

                                // If Updated dates are equal or both null, compare Created dates
                                if (currentCreated && mostRecentCreated) {
                                    return currentCreated > mostRecentCreated ? current : mostRecent;
                                }
                                if (currentCreated && !mostRecentCreated) return current;
                                if (!currentCreated && mostRecentCreated) return mostRecent;

                                return mostRecent;
                            });
                        });
                    })()
                } : teamData;
                
                // Cache the data
                setCachedTeamUserData(deduplicatedTeamData);
                setCachedCostDetailData(costDetailData);
                setCachedOpenCommitmentsData(openCommitmentsData);
                setCachedBillingsData(billingsData);
                setCachedEarnedRevenueData(earnedRevenueData);
                setCachedLaborHoursByWeekData(laborHoursByWeekData);
                setCacheKey(currentCacheKey);
            }
            console.log('cost detail user:', costDetailData)
            
            // Combine teamData and burnToolData for processing
            //const combinedUserData = [...(teamData?.data || []), ...(burnToolData?.data || [])];

            const forecastedHoursByMonth = {};
            const jobMetricsData = calculateMetrics(teamJobs, teamData?.data, costDetailData?.data, openCommitmentsData?.data, billingsData?.data, earnedRevenueData?.data, forecastedHoursByMonth);
            generateChartData(laborHoursByWeekData?.data, forecastedHoursByMonth);
            if (jobMetricsData) {
                generateJobKPIs(teamJobs, teamData?.data, costDetailData?.data, jobMetricsData);
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            setIsLoading(false);
        }
    };

    const calculateMetrics = (jobs, userData, costDetailData, openCommitmentsData, billingsData, earnedRevenueData, forecastedHoursByMonth) => {
        console.log('jobs for metrics calculation:', jobs);
        if (!jobs || !Array.isArray(jobs)) return;
        
        // Clear forecastedHoursByMonth before recalculating
        Object.keys(forecastedHoursByMonth).forEach(key => delete forecastedHoursByMonth[key]);

        // Calculate active projects count
        const activeJobs = jobs.filter(job => job.Job_Status == 'A').length;
        const pendingJobs = jobs.filter(job => job.Job_Status == 'P').length;

        // Build lookup objects for each job (same logic as summary.js)
        const curBudget = {};
        const actualCost = {};
        const openCommitments = {};
        const etc = {};

        const chOrValues = {}
        const dayZeroValues = {}
        const mechanicallyCompleteDates = {}
        const startDates = {}
        const completionDates = {}
        const physicalComplete = {}

        const phasedEquipment = {}
        const phasedLabor = {}
        const laborValues = {}
        const equipmentValues = {}
        const contingData = {}
        const etcValues = {}
        const etcRow = []

        userData.forEach(d => {
            if (d?.Forecast_Tab === 'Change Order') {
                if (chOrValues[d?.BU_ID]) {
                    chOrValues[d?.BU_ID].push({ ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label })
                } else {
                    chOrValues[d?.BU_ID] = [{
                        [d?.Forecast_User_Data_GUID]: { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                    }]
                }
            }

            if (d?.Forecast_Tab === 'Day Zero Budget') {
                if (dayZeroValues[d?.BU_ID] && dayZeroValues[d?.BU_ID][d?.Forecast_Row_ID]) {
                    dayZeroValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else if (dayZeroValues[d?.BU_ID]) {
                    dayZeroValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else {
                    dayZeroValues[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: d
                    }
                }
            }

            if (d?.Forecast_Tab === 'All' || d?.Forecast_Tab === 'Forecast' || d?.Forecast_Tab === 'Set Dates') {
                if (d?.Forecast_Field_Name === 'mechanicallyComplete') {
                    mechanicallyCompleteDates[d?.BU_ID] = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                } else if (d?.Forecast_Field_Name === 'startDateActual') {
                    startDates[d?.BU_ID] = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                } else if (d?.Forecast_Field_Name === 'completionDateForecast') {
                    completionDates[d?.BU_ID] = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                } else if (d?.Forecast_Field_Name === 'physicalComplete') {
                    physicalComplete[d?.BU_ID] = d?.Forecast_Value
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Labor') {
                if (laborValues[d?.BU_ID]) {
                    laborValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else {
                    laborValues[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: d
                    }
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Equipment') {
                if (equipmentValues[d?.BU_ID]) {
                    equipmentValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else {
                    equipmentValues[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: d
                    }
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Equipment Phased') {
                if (phasedEquipment[d?.BU_ID]?.[d?.Forecast_Row_ID]) {
                    phasedEquipment[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else if (phasedEquipment[d?.BU_ID]) {
                    phasedEquipment[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else {
                    phasedEquipment[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    }
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                if (phasedLabor[d?.BU_ID]?.[d?.Forecast_Row_ID]) {
                    phasedLabor[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else if (phasedLabor[d?.BU_ID]) {
                    phasedLabor[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else {
                    phasedLabor[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    }
                }
            }

            if (d?.Forecast_Tab === 'Contingency') {
                if (contingData[d?.BU_ID]?.[d?.Forecast_Row_ID]) {
                    contingData[d?.BU_ID][d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                } else if (contingData[d?.BU_ID]) {
                    contingData[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        [d.Forecast_Field_Name]: d.Forecast_Value
                    }
                } else {
                    contingData[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }
            }

            // Match bidForecast: count every saved ETC row regardless of which tab wrote it
            if (d?.Cost_Type && d?.Forecast_Field_Name == 'etc' && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                if (etcValues[d?.BU_ID]?.[d?.Cost_Type]) {
                    etcValues[d?.BU_ID][d?.Cost_Type] = etcValues[d?.BU_ID][d?.Cost_Type] + pNum(d?.Value_Amount)
                } else if (etcValues[d?.BU_ID]) {
                    etcValues[d?.BU_ID][d?.Cost_Type] = pNum(d?.Value_Amount)
                } else {
                    etcValues[d?.BU_ID] = {
                        [d?.Cost_Type]: pNum(d?.Value_Amount)
                    }
                }
            }

        })

        let etcLabor = {}
        let etcLaborDetails = {}
        let etcIncentive = {}
        let etcEquipment = {}
        let etcMaterials = {}
        let etcRentals = {}
        let etcSubcontractors = {}
        let etcOther = {}
        let etcContingency = {}
        let totalContingency = 0
        let etcTotal = {}
        let hours = {}
        let budgets = {}

        const tmJobIds = new Set(jobs.filter(j => j.Cat_Code_BU_08 === 'T&M').map(j => j.BU_ID));
        jobs.forEach((j) => {
            const job = j.BU_ID
            const isTM = tmJobIds.has(job);
            let regularTimeCostType = 0
            let equipmentCostType = 0
            let equipmentETC = 0
            let equip = {}
            const added = {}
            let regularTime = 0
            let regularHours = 0
            let etc = 0
            const pC = completionDates[job]


            equipmentValues[job] && Object.keys(equipmentValues[job]).forEach((key) => {
                if (key.length == 10 && moment(key).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(pC).endOf('week').isAfter(key)) {
                    const row = equipmentValues[job]?.[key]
                    equipmentCostType = equipmentCostType + ((isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours)))
                }
            })

            laborValues[job] && Object.values(laborValues[job]).forEach((row) => {
                if (row) {
                    const week = row?.Forecast_Row_ID ? row.Forecast_Row_ID?.split('*')[0] : ''
                    const pC = completionDates[job]
                    if (week && moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(pC).isAfter(week)) {
                        regularTimeCostType = regularTimeCostType + (!isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0)
                        const weekHours = (!isNaN(pNum(row.Number_Hours)) && !isNaN(pNum(row.Number_Units)) ? pNum(row.Number_Hours) * pNum(row.Number_Units) : 0)
                        regularHours = regularHours + weekHours
                    }

                    if (week && !isTM) {
                        const monthKey = moment(week).format('YYYY-MM')
                        const weekHours = (!isNaN(pNum(row.Number_Hours)) && !isNaN(pNum(row.Number_Units)) ? pNum(row.Number_Hours) * pNum(row.Number_Units) : 0)
                        forecastedHoursByMonth[monthKey] = (forecastedHoursByMonth[monthKey] || 0) + weekHours
                    }
                }
            })
            //if(job == '54025076') console.log('phased labor', phasedLabor[job]);
            phasedLabor[job] && Object.keys(phasedLabor[job]).forEach((row) => {
                const weekRow = row.includes('=')
                const week = row.split('=')[1]
                const phase = row.split('=')[0]
                const pC = completionDates[job]
                //const phaseDetails = phase ? phasedLabor[job][phase] : {}
                const withinProject = moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pC).endOf('week'))
                const withinStartStop = true//moment(week).isAfter(moment(phaseDetails?.startDate).startOf('week')) && moment(week).isBefore(moment(phaseDetails?.endDate).endOf('week'))
                const h = phasedLabor[job][row]?.Number_Hours ? phasedLabor[job][row].Number_Hours : 0
                const bR = phasedLabor[job][row]?.Rate ? phasedLabor[job][row].Rate : 0
                const cS = phasedLabor[job][row]?.Number_Units ? phasedLabor[job][row].Number_Units : 0
                //if(job == '54025076') console.log('phased labor', job, row, week, phase, h, bR, cS, withinProject, withinStartStop, pC, versionInfo?.Version_Start_DateTime);
                if (weekRow && phasedLabor[job][row] && h && bR && cS && withinProject && withinStartStop) {
                    const weekHours = (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    regularHours = regularHours + weekHours
                    regularTime = regularTime + (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0)
                }

                if (weekRow && phasedLabor[job][row] && h && bR && cS && !isTM) {
                    const monthKey = moment(week).format('YYYY-MM')
                    const weekHours = (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    forecastedHoursByMonth[monthKey] = (forecastedHoursByMonth[monthKey] || 0) + weekHours
                }
            })

            phasedEquipment[job] && Object.keys(phasedEquipment[job]).forEach((t) => {
                const r = phasedEquipment[job][t]
                const pC = completionDates[job]
                const v = versionInfo?.Version_Start_DateTime

                // Row IDs use '=' (new) or '*' (legacy) between name and week
                const _sep = t.includes('=') ? '=' : '*'
                const _idx = t.lastIndexOf(_sep)
                const w = _idx >= 0 ? t.substring(_idx + 1) : undefined
                const e = _idx >= 0 ? t.substring(0, _idx) : t
                const rate = phasedEquipment[job][e]?.Rate ? phasedEquipment[job][e].Rate : r?.Rate ? r.Rate : 0
                const h = r?.Number_Hours
                const n = r?.Number_Units
                //console.log('e1', job, t, rate, equipmentUserData[job][t])
                if (w && h && n && rate && moment(w).isAfter(moment(v).startOf('week')) && moment(w).isBefore(moment(pC).endOf('week'))) {
                    if (!equip[e]) equip[e] = {}
                    if (!equip[e][w]) equip[e][w] = { rate: rate, h: isNaN(pNum(h)) ? 0 : pNum(h), n: isNaN(pNum(n)) ? 0 : pNum(n), total: 0 }
                    equip[e][w].rate = rate
                    equip[e][w].h = equip[e][w].h + (isNaN(pNum(h)) ? 0 : pNum(h))
                    equip[e][w].n = equip[e][w].n + (isNaN(pNum(n)) ? 0 : pNum(n))
                    equip[e][w].total = equip[e][w].rate * equip[e][w].h * equip[e][w].n

                    //console.log('e2', e, job, w, rate, equipRates[e], equipmentRates[e], equipmentETC, (!isNaN(pNum(rate)) && !isNaN(pNum(h)) && !isNaN(pNum(n)) ? pNum(rate) * pNum(h) * pNum(n) : 0))
                    equipmentETC = equipmentETC + (!isNaN(pNum(rate)) && !isNaN(pNum(h)) && !isNaN(pNum(n)) ? pNum(rate) * pNum(h) * pNum(n) : 0)

                    if (!added[e]) {
                        added[e] = [{ w: w, cur: equipmentETC, rate: equip[e][w].rate, n: equip[e][w].n, h: equip[e][w].h }]
                    } else {
                        added[e].push({ w: w, cur: equipmentETC, rate: equip[e][w].rate, n: equip[e][w].n, h: equip[e][w].h })
                    }

                }
            })

            let labor = 0
            let materials = 0
            let equipment = 0
            let rentals = 0
            let subcontractors = 0
            let other = 0
            let contingency = 0
            let total = 0

            etcValues[job] && Object.keys(etcValues[job]).forEach((row) => {
                etc = etc + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                //if(job == '54024086') console.log(etcValues[job][row][0], job, row, labor, rentals, materials, subcontractors, other);
                if (row[0] == '5' && row[1] == '1' && (row[2] == '6' || row[2] == '7' || row[2] == '8')) {
                    labor = labor + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && row[1] == '2') {
                    rentals = rentals + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && row[1] == '3') {
                    materials = materials + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && row[1] == '4') {
                    subcontractors = subcontractors + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                    if (job == '12425029') console.log('29 sub', etcValues[job][row], subcontractors);
                } else if (row[0] == '5' && (row[1] == '5' || row[1] == '6' || row[1] == '7')) {
                    if (job == '54025036') console.log('36 other', etcValues, row, etcValues[job][row], other);
                    other = other + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                }
            })

            let totalcLabor = 0
            let totalcEquipment = 0
            let totalcMaterials = 0
            let totalcSubcontractors = 0
            let totalcOtherExpenses = 0
            let totalcTotal = 0

            contingData[job] && Object.values(contingData[job]).forEach((row) => {
                totalcLabor = totalcLabor + (isNaN(pNum(row.CurrentLabor)) ? 0 : pNum(row.CurrentLabor))
                totalcEquipment = totalcEquipment + (isNaN(pNum(row.CurrentEquipment)) ? 0 : pNum(row.CurrentEquipment))
                totalcMaterials = totalcMaterials + (isNaN(pNum(row.CurrentMaterials)) ? 0 : pNum(row.CurrentMaterials))
                totalcSubcontractors = totalcSubcontractors + (isNaN(pNum(row.CurrentSubs)) ? 0 : pNum(row.CurrentSubs))
                totalcOtherExpenses = totalcOtherExpenses + (isNaN(pNum(row.CurrentOther)) ? 0 : pNum(row.CurrentOther))
            })
            totalcTotal = totalcLabor + totalcEquipment + totalcMaterials + totalcSubcontractors + totalcOtherExpenses

            //totalCost[job] = equipmentETC + regularTime + etc + totalcTotal
            hours[job] = regularHours
            etcLaborDetails[job] = { labor: labor, regularTimeCostType: regularTimeCostType, regularTime: regularTime }
            etcLabor[job] = labor + regularTimeCostType + regularTime
            etcIncentive[job] = labor
            etcEquipment[job] = equipmentCostType + equipmentETC
            etcMaterials[job] = materials
            etcRentals[job] = rentals
            etcSubcontractors[job] = subcontractors
            etcOther[job] = other
            etcContingency[job] = totalcTotal
            totalContingency = totalContingency + totalcTotal
            if (job == '54025076') console.log('etc total', job, labor, regularTimeCostType, regularTime, equipmentCostType, equipmentETC, materials, rentals, subcontractors, other, totalcTotal);
            etcTotal[job] = labor + regularTimeCostType + regularTime + equipmentCostType + equipmentETC + materials + rentals + subcontractors + other + totalcTotal

            let jobCurBudget = 0
            dayZeroValues[job] && Object.values(dayZeroValues[job]).forEach((row) => {
                    if (row['Cost_Type'] && row['Cost_Type'].length) {
                        if (row['Cost_Type'][0] == '4') {
                        jobCurBudget = jobCurBudget + pNum(row['Value_Amount'])
                    }
                }
            })

            console.log('ch or values', job, chOrValues[job]);

            chOrValues[job] && chOrValues[job].forEach((row) => {
                if (row.Status === 'Approved') {
                    jobCurBudget = jobCurBudget + (isNaN(pNum(row['41000'])) ? 0 : pNum(row['41000']))
                    // jobCurBudget = jobCurBudget + (isNaN(pNum(row.tAndM)) ? 0 : pNum(row.tAndM))
                    // jobCurBudget = jobCurBudget + (isNaN(pNum(row.changeOrders)) ? 0 : pNum(row.changeOrders))
                    // jobCurBudget = jobCurBudget + (isNaN(pNum(row.reductionOfScope)) ? 0 : pNum(row.reductionOfScope))
                }
            })
            //console.log('change orders', job.BU_ID, jobCurBudget);
            curBudget[job] = jobCurBudget > 0 ? fNum(jobCurBudget, 1) : ''
            budgets[job] = jobCurBudget

        })

        // Calculate actual costs from costDetailData (same as summary.js lines 656-688)
        const actualCostObj = {};
        if (Array.isArray(costDetailData)) {
            console.log('actual cost data:', costDetailData);
            costDetailData.forEach(d => {
                // Filter for 'Job Cost - All Others' or 'Job Cost - Labor' (same as summary.js)
                if ((d?.Cost_Category == 'Job Cost - All Others' || d?.Cost_Category == 'Job Cost - Labor') && actualCostObj[d?.Job_Number]) {
                    actualCostObj[d?.Job_Number] = actualCostObj[d?.Job_Number] + (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost));
                } else if (d?.Cost_Category == 'Job Cost - All Others' || d?.Cost_Category == 'Job Cost - Labor') {
                    actualCostObj[d?.Job_Number] = (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost));
                }
            });
        }
        // Copy to actualCost for each job
        jobs.forEach(job => {
            actualCost[job.BU_ID] = actualCostObj[job.BU_ID] || 0;
        });

        // Calculate open commitments from openCommitmentsData (same as summary.js lines 704-714)
        const openCommitmentsObj = {};
        if (Array.isArray(openCommitmentsData)) {
            openCommitmentsData.forEach(d => {
                // Filter for Cost_Type_Code starting with '5' (same as summary.js)
                if (d?.Cost_Type_Code?.[0] == '5' && openCommitmentsObj[d?.Job_Number]) {
                    openCommitmentsObj[d?.Job_Number] = openCommitmentsObj[d?.Job_Number] + (isNaN(pNum(d?.PO_True_Open_Amount)) ? 0 : pNum(d?.PO_True_Open_Amount));
                } else if (d?.Cost_Type_Code?.[0] == '5') {
                    openCommitmentsObj[d?.Job_Number] = (isNaN(pNum(d?.PO_True_Open_Amount)) ? 0 : pNum(d?.PO_True_Open_Amount));
                }
            });
        }
        // Copy to openCommitments for each job
        jobs.forEach(job => {
            openCommitments[job.BU_ID] = openCommitmentsObj[job.BU_ID] || 0;
        });

        // Calculate total backlog using summary.js formula (lines 876-897)
        let totalBacklog = 0;
        const backlogDetailsList = [];
        console.log('etc total:', etcTotal);
        jobs.forEach(job => {
            // Calculate EAC: actualCost + openCommitments + ETC
            const total = pNum(actualCost[job.BU_ID]) + pNum(openCommitments[job.BU_ID]) + pNum(etcTotal[job.BU_ID]);

            // Calculate backlog: (1 - (actualCost / total)) * curBudget
            if (total > 0 && budgets[job.BU_ID] > 0) {
                const jobBacklog = (1 - (pNum(actualCost[job.BU_ID]) / total)) * pNum(budgets[job.BU_ID]);
                totalBacklog += jobBacklog;

                backlogDetailsList.push({
                    jobId: job.BU_ID,
                    description: job.BU_Description || job.Job_Description || 'N/A',
                    budget: budgets[job.BU_ID],
                    actualCost: actualCost[job.BU_ID],
                    openCommitments: openCommitments[job.BU_ID],
                    etc: etcTotal[job.BU_ID],
                    etcLabor: etcLabor[job.BU_ID] || 0,
                    etcEquipment: etcEquipment[job.BU_ID] || 0,
                    etcMaterials: etcMaterials[job.BU_ID] || 0,
                    etcRentals: etcRentals[job.BU_ID] || 0,
                    etcSubcontractors: etcSubcontractors[job.BU_ID] || 0,
                    etcOther: etcOther[job.BU_ID] || 0,
                    etcContingency: etcContingency[job.BU_ID] || 0,
                    eac: total,
                    backlog: jobBacklog,
                    percentComplete: total > 0 ? (actualCost[job.BU_ID] / total * 100) : 0
                });
            }
        });

        setTotalBacklogDetails(backlogDetailsList.sort((a, b) => b.backlog - a.backlog));

        // Calculate billings from loadCostDetailBillings (following summary.js model)
        const billings = {};
        if (Array.isArray(earnedRevenueData)) {
            earnedRevenueData.forEach(d => {
                billings[d?.Job_Number] = -d.Total_Job_Cost;
            });
        }

        // Calculate earned revenue based on cost percentage (following summary.js model)
        const earnedRevenue = {};
        jobs.forEach(job => {
            const jobActualCost = pNum(actualCost[job.BU_ID]);
            const jobOpenCommitments = pNum(openCommitments[job.BU_ID]);
            const jobEtc = pNum(etcTotal[job.BU_ID]);
            const total = jobActualCost + jobOpenCommitments + jobEtc;
            const costPercent = total > 0 ? (jobActualCost / total) * 100 : 0;
            earnedRevenue[job.BU_ID] = (costPercent / 100) * pNum(budgets[job.BU_ID]);
        });

        // Calculate over/under billing details (following summary.js model) - filter out T&M jobs
        const overUnderBillingList = jobs
            .filter(job => job.Cat_Code_BU_08 != 'T&M')
            .map(job => {
                const jobBillings = pNum(billings[job.BU_ID]);
                const jobEarnedRev = pNum(earnedRevenue[job.BU_ID]);
                const overUnder = jobBillings - jobEarnedRev;

                return {
                    jobId: job.BU_ID,
                    description: job.BU_Description || job.Job_Description || 'N/A',
                    earnedRevenue: jobEarnedRev,
                    billings: jobBillings,
                    overUnder: overUnder,
                    status: overUnder > 0 ? 'Over Billed' : overUnder < 0 ? 'Under Billed' : 'On Track'
                };
            }).filter(j => j.earnedRevenue > 0 || j.billings > 0);

        setOverUnderBillingDetails(overUnderBillingList.sort((a, b) => Math.abs(b.overUnder) - Math.abs(a.overUnder)));

        // Calculate total over/under billing from the filtered list
        const totalOverUnderBilling = overUnderBillingList.reduce((sum, job) => sum + job.overUnder, 0);

        // Set active projects details now that curBudget is calculated
        const activeJobsList = jobs.filter(job => job.Job_Status == 'A' || job.Job_Status == 'P');
        setActiveProjectsDetails(activeJobsList.map(job => ({
            jobId: job.BU_ID,
            description: job.BU_Description || job.Job_Description || 'N/A',
            status: job.Job_Status == 'A' ? 'Active' : 'Pending',
            startDate: job.Start_Date ? moment(job.Start_Date).format('M/D/YYYY') : 'N/A',
            budget: curBudget[job.BU_ID] || 0
        })).sort((a, b) => {
            if (a.status === 'Active' && b.status !== 'Active') return -1;
            if (a.status !== 'Active' && b.status === 'Active') return 1;
            return a.jobId.localeCompare(b.jobId);
        }));

        // Calculate jobs due for billing based on billing frequency and last billed date
        const mostRecentBill = {};

        // Get most recent billing date per job
        const billingsByJob = {};
        if (Array.isArray(billingsData)) {
            billingsData.forEach(d => {
                if (billingsByJob[d?.Job_Number]) {
                    billingsByJob[d?.Job_Number].push(d);
                } else {
                    billingsByJob[d?.Job_Number] = [d];
                }
            });

            Object.values(billingsByJob).forEach(bills => {
                let mostRecentCostDate = '2021-01-01';
                bills.forEach(d => {
                    if (d?.GL_Date && moment(d?.GL_Date).isAfter(moment(mostRecentCostDate))) {
                        mostRecentCostDate = d?.GL_Date;
                    }
                });
                mostRecentBill[bills[0]?.Job_Number] = mostRecentCostDate;
            });
        }

        // Calculate week number helper function
        const getWeekNumber = (date) => {
            const d = moment(date);
            return d.week();
        };

        // Count jobs due for billing using the provided logic
        let jobsDueForBilling = 0;
        const jobsDueDetails = [];
        const allJobsDetails = [];

        console.log('Calculating jobs due for billing...', jobs)
        jobs.forEach(job => {
            const billingType = job.Cat_Code_BU_10_Description; // Billing frequency
            const estEarnedRevenue = earnedRevenue[job.BU_ID] || 0;
            const lastBilledDate = mostRecentBill[job.BU_ID];

            let isDue = false;
            let dueStatus = 'Not Due';

            // Skip if billing type is "On Completion"
            if (billingType === 'On Completion') {
                dueStatus = 'On Completion';
            }
            // Only consider jobs with earned revenue >= 20000
            // else if (estEarnedRevenue < 20000) {
            //     dueStatus = 'Below Threshold';
            // }
            else if (!lastBilledDate) {
                // No billing history - job is due
                isDue = true;
                dueStatus = 'Due (Never Billed)';
            }
            else {
                const currentWeek = getWeekNumber(new Date());
                const lastBilledWeek = getWeekNumber(lastBilledDate);
                const weeksSinceLastBilled = Math.abs(currentWeek + 52 - lastBilledWeek);

                switch (billingType) {
                    case 'Bi-Weekly':
                        isDue = weeksSinceLastBilled > 2;
                        break;
                    case 'Monthly':
                    case 'Milestone':
                        isDue = weeksSinceLastBilled > 5;
                        break;
                    case 'Weekly':
                        isDue = weeksSinceLastBilled > 1;
                        break;
                    default:
                        isDue = false;
                }

                if (isDue) {
                    dueStatus = 'Due';
                } else {
                    dueStatus = 'Not Due';
                }
            }

            if (isDue) {
                jobsDueForBilling++;
            }

            // Add all jobs to the details array
            allJobsDetails.push({
                jobId: job.BU_ID,
                description: job.BU_Description,
                lastBilledDate: lastBilledDate ? moment(lastBilledDate).format('M/D/YYYY') : 'Never',
                billingFrequency: billingType || 'N/A',
                earnedRevenue: estEarnedRevenue,
                isDue: isDue,
                dueStatus: dueStatus
            });
        });

        // Sort jobs: due jobs first, then by job ID
        allJobsDetails.sort((a, b) => {
            if (a.isDue && !b.isDue) return -1;
            if (!a.isDue && b.isDue) return 1;
            return a.jobId.localeCompare(b.jobId);
        });

        console.log('all jobs billing details:', allJobsDetails);
        setJobsDueForBillingDetails(allJobsDetails);

        // Calculate jobs finishing in next 30 days - show all jobs sorted by completion date (soonest first)
        const next30Days = moment().add(30, 'days');
        const jobsFinishingList = jobs
            .filter(job => completionDates[job.BU_ID])
            .map(job => {
                const completeDate = moment(completionDates[job.BU_ID], 'M/D/YYYY');
                const daysUntilComplete = completeDate.diff(moment(), 'days');
                return {
                    jobId: job.BU_ID,
                    description: job.BU_Description || job.Job_Description || 'N/A',
                    completeDate: completionDates[job.BU_ID],
                    daysUntilComplete: daysUntilComplete,
                    status: job.Job_Status || 'N/A'
                };
            })
            .sort((a, b) => a.daysUntilComplete - b.daysUntilComplete);
        
        const jobsFinishingFiltered = jobsFinishingList.filter(j => j.daysUntilComplete <= 30);
        const jobsFinishing = jobsFinishingFiltered.length;
        setJobsFinishingDetails(jobsFinishingFiltered);

        // Calculate jobs starting in next 4 weeks - show all jobs sorted by start date (farthest in future first)
        const next4Weeks = moment().add(4, 'weeks');
        const jobsStartingList = jobs
            .filter(job => startDates[job.BU_ID])
            .map(job => {
                const startDate = moment(startDates[job.BU_ID], 'M/D/YYYY');
                const daysUntilStart = startDate.diff(moment(), 'days');
                return {
                    jobId: job.BU_ID,
                    description: job.BU_Description || job.Job_Description || 'N/A',
                    startDate: startDates[job.BU_ID],
                    daysUntilStart: daysUntilStart,
                    status: job.Job_Status || 'N/A'
                };
            })
            .sort((a, b) => b.daysUntilStart - a.daysUntilStart);
        
        const jobsStarting = jobsStartingList.filter(j => j.daysUntilStart >= 0 && j.daysUntilStart <= 28).length;
        setJobsStartingDetails(jobsStartingList);

        // Calculate new jobs in last 30 days - show all jobs sorted by most recent start date first
        const last30Days = moment().subtract(30, 'days');
        const newJobsList = jobs
            .map(job => {
                const startDate = startDates[job.BU_ID] ? moment(startDates[job.BU_ID], 'M/D/YYYY') : null;
                const daysOld = startDate ? moment().diff(startDate, 'days') : null;
                return {
                    jobId: job.BU_ID,
                    description: job.BU_Description || job.Job_Description || 'N/A',
                    createdDate: startDates[job.BU_ID] || 'N/A',
                    daysOld: daysOld,
                    status: job.Job_Status || 'N/A'
                };
            })
            .sort((a, b) => {
                if (a.daysOld === null) return 1;
                if (b.daysOld === null) return -1;
                return a.daysOld - b.daysOld;
            });
        
        const newJobs = newJobsList.filter(j => j.daysOld !== null && j.daysOld <= 30).length;
        setNewJobsDetails(newJobsList);

        // Generate jobs finishing chart data (weekly buckets for next 5 weeks)
        const weeklyFinishingData = [];
        const weekLabels = [];
        const weeklyJobIds = [];
        let totalJobsInChart = 0;
        
        for (let i = 0; i < 5; i++) {
            const weekStart = moment().add(i * 7, 'days');
            const weekEnd = moment().add((i + 1) * 7, 'days');
            weekLabels.push(`${weekStart.format('M/D')}-${weekEnd.format('M/D')}`);
            
            const jobsInWeek = jobsFinishingFiltered.filter(job => {
                const daysUntil = job.daysUntilComplete;
                return daysUntil >= (i * 7) && daysUntil < ((i + 1) * 7);
            });
            
            weeklyFinishingData.push(jobsInWeek.length);
            weeklyJobIds.push(jobsInWeek.map(j => j.jobId).join(', '));
            totalJobsInChart += jobsInWeek.length;
        }

        setChartData(prev => ({
            ...prev,
            jobsFinishingChart: {
                labels: weekLabels,
                datasets: [{
                    data: weeklyFinishingData.length > 0 ? weeklyFinishingData : [0, 0, 0, 0, 0],
                    jobIds: weeklyJobIds,
                    backgroundColor: alpha(SERIES[0], 0.8),
                    borderColor: SERIES[0],
                    borderWidth: 0
                }],
                totalJobs: totalJobsInChart
            }
        }));

        // Update metrics
        setMetrics({
            totalBacklog: totalBacklog > 0 ? fNum(totalBacklog, 1) : '0',
            activeProjects: { active: activeJobs, pending: pendingJobs },
            overUnderBilling: fNum(totalOverUnderBilling, 1),
            jobsDueForBill: jobsDueForBilling,
            jobsFinishing: jobsFinishing,
            jobsStartingNext4Weeks: jobsStarting,
            newJobsLast30Days: newJobs
        });

        // Return calculated data for generateJobKPIs
        return {
            actualCost,
            openCommitments,
            etcTotal,
            curBudget: budgets,
            completionDates,
            startDates,
            billings,
            earnedRevenue: overUnderBillingList.reduce((acc, job) => {
                acc[job.jobId] = job.earnedRevenue;
                return acc;
            }, {}),
            overUnder: overUnderBillingList.reduce((acc, job) => {
                acc[job.jobId] = job.overUnder;
                return acc;
            }, {})
        };
    };

    const generateChartData = (laborHoursByWeek, forecastedHoursByMonth) => {
        // Generate labor hours trend data with actual and forecasted hours
        const labels = [];
        const actualHoursData = [];
        const forecastedHoursHistorical = [];
        const forecastedHoursFuture = [];
        // Group actual hours by month from weekly data and track week counts
        const actualHoursByMonth = {};
        const weekCountByMonth = {};
        if (laborHoursByWeek && Array.isArray(laborHoursByWeek)) {
            laborHoursByWeek.forEach(item => {
                if (item.Week_Monday && item.Total_Hours) {
                    const monthKey = moment(item.Week_Monday).format('YYYY-MM');
                    actualHoursByMonth[monthKey] = (actualHoursByMonth[monthKey] || 0) + parseFloat(item.Total_Hours);
                    weekCountByMonth[monthKey] = (weekCountByMonth[monthKey] || 0) + 1;
                }
            });
        }

        // Get the current week of the month to determine if current month is complete
        const currentWeekOfMonth = Math.ceil(moment().date() / 7);
        const currentMonthKey = moment().format('YYYY-MM');

        // Historical months (last 6 months) - show both actual and forecast
        for (let i = 8; i >= 0; i--) {
            const date = moment().subtract(i, 'months');
            const monthKey = date.format('YYYY-MM');
            labels.push(date.format('MMM'));

            const forecastHours = forecastedHoursByMonth?.[monthKey] || 0;
            
            // Only show actual hours if the month is complete (not current month or has at least 4 weeks)
            let actualHours = 0;
            if (monthKey === currentMonthKey) {
                // Current month - don't show actual hours since it's incomplete
                actualHours = null;
            } else if (actualHoursByMonth[monthKey]) {
                // Past month - show if we have data
                actualHours = Math.round(actualHoursByMonth[monthKey] / 1000);
            } else {
                actualHours = null;
            }
            
            actualHoursData.push(actualHours);
            forecastedHoursHistorical.push(Math.round(forecastHours / 1000)); // Show forecast for comparison
            forecastedHoursFuture.push(i == 0 ? Math.round(forecastHours / 1000) : null); // No future forecast in historical period
        }

        // Future months (next 6 months) - show only forecast
        for (let i = 1; i < 5; i++) {
            const date = moment().add(i, 'months');
            const monthKey = date.format('YYYY-MM');
            labels.push(date.format('MMM'));

            const forecastHours = forecastedHoursByMonth?.[monthKey] || 0;
            
            actualHoursData.push(null); // No actual data for future months
            forecastedHoursHistorical.push(null); // Historical forecast ends
            forecastedHoursFuture.push(Math.round(forecastHours / 1000)); // Future forecast
        }

        setChartData(prev => ({
            ...prev,
            laborHoursTrend: {
                labels: labels,
                datasets: [
                    {
                        label: 'Actual Hours',
                        data: actualHoursData,
                        borderColor: SERIES[0],
                        backgroundColor: 'transparent',
                        borderWidth: 2,
                        pointRadius: 0,
                        tension: 0.1
                    },
                    {
                        label: 'Estimated Hours (Historical)',
                        data: forecastedHoursHistorical,
                        borderColor: SERIES[1],
                        backgroundColor: alpha(SERIES[1], 0.1),
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0.1
                    },
                    {
                        label: 'Forecasted Hours (Future)',
                        data: forecastedHoursFuture,
                        borderColor: SERIES[0],
                        backgroundColor: 'transparent',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        pointRadius: 0,
                        tension: 0.1
                    }
                ]
            }
        }));
    };

    const generateJobKPIs = (jobs, userData, costData, metricsData) => {
        const jobKPIData = jobs.map(job => {
            // Calculate EAC: actualCost + openCommitments + ETC
            const actualCost = pNum(metricsData.actualCost[job.BU_ID]) || 0;
            const openComm = pNum(metricsData.openCommitments[job.BU_ID]) || 0;
            const etc = pNum(metricsData.etcTotal[job.BU_ID]) || 0;
            const eac = actualCost + openComm + etc;

            // Get current budget
            const budget = pNum(metricsData.curBudget[job.BU_ID]) || 0;

            // Calculate GM%: (Budget - EAC) / Budget * 100
            const gm = budget > 0 ? ((budget - eac) / budget * 100) : 0;

            // Calculate complete%: actualCost / EAC * 100
            const completePercent = eac > 0 ? (actualCost / eac * 100) : 0;

            // Get over/under billing amount
            const overUnder = metricsData.overUnder[job.BU_ID] || 0;

            // Get completion date
            const endDate = metricsData.completionDates[job.BU_ID] || 'N/A';
            
            // Get start date
            const startDate = metricsData.startDates[job.BU_ID] || 'N/A';

            return {
                jobId: job.BU_ID,
                jobName: `${job.BU_ID} - ${job.BU_Description || 'N/A'}`,
                eac: Math.round(eac),
                gm: gm,
                completePercent: completePercent,
                overUnder: Math.round(overUnder),
                startDate: startDate,
                endDate: endDate,
                endDateSort: endDate === 'N/A' ? null : moment(endDate, 'M/D/YYYY')
            };
        });

        // Sort by job ID descending (highest first)
        jobKPIData.sort((a, b) => {
            const jobIdA = parseInt(a.jobId) || 0;
            const jobIdB = parseInt(b.jobId) || 0;
            return jobIdB - jobIdA;
        });

        setJobKPIs(jobKPIData);
    };

    const getBillingStatusStyle = (status) => {
        switch (status) {
            case 'Over Billed':
                return { backgroundColor: '#FFE6E6', color: '#D32F2F', borderColor: '#D32F2F' };
            case 'Under Billed':
                return { backgroundColor: '#FFF3E0', color: '#F57C00', borderColor: '#F57C00' };
            default:
                return { backgroundColor: '#E8F5E8', color: '#388E3C', borderColor: '#388E3C' };
        }
    };

    const screenWidth = Dimensions.get('window').width;
    const chartWidth = screenWidth - 300 - 40; // Account for sidebar and padding

    const styles = StyleSheet.create({
        container: {
            backgroundColor: '#f5f7fa',
            minHeight: '100%'
        },
        content: {
            padding: 30,
            maxWidth: screenWidth - 300,
        },
        metricsGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginBottom: 20,
            gap: 10,
        },
        metricCard: {
            backgroundColor: 'white',
            borderRadius: 6,
            padding: 12,
            minWidth: 150,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
            cursor: 'pointer',
        },
        metricTitle: {
            fontSize: 10,
            color: '#666',
            marginBottom: 6,
            fontWeight: '500',
        },
        metricValue: {
            fontSize: 20,
            fontWeight: 'bold',
            color: '#333',
        },
        metricSubValue: {
            fontSize: 16,
            color: '#888',
            marginTop: 4,
        },
        chartsSection: {
            flexDirection: 'row',
            marginBottom: 20,
            gap: 15,
        },
        chartCard: {
            backgroundColor: 'white',
            borderRadius: 6,
            padding: 30,
            flex: 1,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
            justifyContent: 'space-between',
        },
        chartTitle: {
            fontSize: 14,
            fontWeight: '600',
            marginBottom: 10,
            color: '#333',
        },
        contingencySection: {
            marginBottom: 20,
        },
        contingencyGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 10,
        },
        contingencyCard: {
            backgroundColor: 'white',
            borderRadius: 6,
            padding: 12,
            minWidth: 140,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
        },
        contingencyLabel: {
            fontSize: 10,
            color: '#666',
            marginBottom: 4,
        },
        contingencyValue: {
            fontSize: 16,
            fontWeight: 'bold',
            color: '#333',
        },
        kpiSection: {
            backgroundColor: 'white',
            borderRadius: 6,
            padding: 15,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
        },
        kpiTitle: {
            fontSize: 16,
            fontWeight: '600',
            marginBottom: 15,
            color: '#333',
        },
        kpiTable: {
            borderWidth: 1,
            borderColor: '#e0e0e0',
            borderRadius: 6,
        },
        kpiHeader: {
            flexDirection: 'row',
            backgroundColor: '#f8f9fa',
            paddingVertical: 8,
            paddingHorizontal: 6,
        },
        kpiHeaderCell: {
            flex: 1,
            fontSize: 10,
            fontWeight: '600',
            color: '#666',
            textAlign: 'center',
        },
        kpiRow: {
            flexDirection: 'row',
            paddingVertical: 4,
            paddingHorizontal: 4,
            borderTopWidth: 1,
            borderTopColor: '#e0e0e0',
        },
        kpiCell: {
            flex: 1,
            fontSize: 10,
            color: '#333',
            textAlign: 'center',
        },
        kpiJobName: {
            textAlign: 'left',
            fontWeight: '500',
        },
        billingStatus: {
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 4,
            fontSize: 10,
            fontWeight: '500',
            borderWidth: 1,
            textAlign: 'center',
        },
        expandedSection: {
            backgroundColor: 'white',
            borderRadius: 6,
            padding: 20,
            marginBottom: 20,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
        },
        expandedTitle: {
            fontSize: 16,
            fontWeight: '600',
            marginBottom: 15,
            color: '#333',
        },
        billingTable: {
            borderWidth: 1,
            borderColor: '#e0e0e0',
            borderRadius: 6,
        },
        billingHeader: {
            flexDirection: 'row',
            backgroundColor: '#f8f9fa',
            paddingVertical: 10,
            paddingHorizontal: 12,
            borderTopLeftRadius: 6,
            borderTopRightRadius: 6,
        },
        billingHeaderCell: {
            fontSize: 11,
            fontWeight: '600',
            color: '#666',
        },
        billingRow: {
            flexDirection: 'row',
            paddingVertical: 10,
            paddingHorizontal: 12,
            borderTopWidth: 1,
            borderTopColor: '#e0e0e0',
        },
        billingCell: {
            fontSize: 11,
            color: '#333',
        },
        closeButton: {
            padding: 8,
            borderRadius: 4,
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
        },
        loadingContainer: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f7fa',
            minHeight: '100vh',
        },
        loadingSpinner: {
            width: 60,
            height: 60,
            borderWidth: 4,
            borderColor: '#e0e0e0',
            borderTopColor: '#4A90E2',
            borderRadius: 30,
            animation: 'spin 1s linear infinite',
        },
        loadingText: {
            marginTop: 20,
            fontSize: 16,
            color: '#666',
            fontWeight: '500',
        },
        loadingSubtext: {
            marginTop: 8,
            fontSize: 14,
            color: '#999',
        },
    });

    const SkeletonBox = ({ width = '100%', height = 20, style = {} }) => (
        <View style={{
            width,
            height,
            backgroundColor: '#e0e0e0',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            ...style
        }}>
            <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                height: '100%',
                width: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                animation: 'shimmer 1.5s infinite'
            }} />
        </View>
    );

    const tabButton = (label) => (
        <Pressable
            onPress={() => setDashView(label)}
            style={{
                paddingTop: 8, paddingBottom: 8, paddingLeft: 16, paddingRight: 16,
                borderBottomWidth: 2,
                borderBottomColor: dashView === label ? '#065F46' : 'transparent',
            }}>
            <Text style={{
                fontSize: 13,
                fontWeight: dashView === label ? '700' : '500',
                color: dashView === label ? '#065F46' : '#6B7280',
            }}>{label}</Text>
        </Pressable>
    );

    return (
        <View style={styles.container}>
            <View style={{ flexDirection: 'row', backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingLeft: 8 }}>
                {tabButton('Overview')}
                {tabButton('Team Schedule')}
            </View>
            {dashView === 'Team Schedule' ? <TeamGantt /> : (
            <ScrollView style={styles.content}>
                {/* Top Metrics Grid */}
                <View style={styles.metricsGrid}>{isLoading ? (
                    <>
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <View key={i} style={styles.metricCard}>
                                <SkeletonBox width="60%" height={10} style={{ marginBottom: 8 }} />
                                <SkeletonBox width="80%" height={24} />
                            </View>
                        ))}
                    </>
                ) : (
                    <>
                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'totalBacklog' ? null : 'totalBacklog')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>Total Backlog</Text>
                            <Text style={styles.metricValue}>{metrics.totalBacklog}</Text>
                        </View>
                    </Pressable>

                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'activeProjects' ? null : 'activeProjects')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>Active Projects</Text>
                            <Text style={styles.metricValue}>
                                {metrics.activeProjects.active + metrics.activeProjects.pending}/
                                {metrics.activeProjects.pending}
                            </Text>
                        </View>
                    </Pressable>

                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'overUnderBilling' ? null : 'overUnderBilling')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>Over / Under Billing</Text>
                            <Text style={[styles.metricValue, {
                                color: metrics.overUnderBilling >= 0 ? '#4CAF50' : '#F44336'
                            }]}>
                                {metrics.overUnderBilling}
                            </Text>
                        </View>
                    </Pressable>

                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'jobsDueForBill' ? null : 'jobsDueForBill')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>Jobs Due for a Bill</Text>
                            <Text style={styles.metricValue}>{metrics.jobsDueForBill}</Text>
                        </View>
                    </Pressable>

                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'jobsFinishing' ? null : 'jobsFinishing')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>Jobs Finishing</Text>
                            <Text style={styles.metricValue}>{metrics.jobsFinishing}</Text>
                        </View>
                    </Pressable>

                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'jobsStarting' ? null : 'jobsStarting')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>Jobs Starting Next 4 weeks</Text>
                            <Text style={[styles.metricValue, {
                                color: metrics.jobsStartingNext4Weeks >= 0 ? '#4CAF50' : '#F44336'
                            }]}>
                                {metrics.jobsStartingNext4Weeks}
                            </Text>
                        </View>
                    </Pressable>

                    <Pressable
                        onPress={() => setExpandedMetric(expandedMetric === 'newJobs' ? null : 'newJobs')}
                    >
                        <View style={styles.metricCard}>
                            <Text style={styles.metricTitle}>New Jobs (Last 30 Days)</Text>
                            <Text style={styles.metricValue}>{metrics.newJobsLast30Days}</Text>
                        </View>
                    </Pressable>
                    </>
                )}
                </View>

                {/* Expanded Total Backlog */}
                {expandedMetric === 'totalBacklog' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                Total Backlog Breakdown - {fNum(totalBacklogDetails.reduce((sum, j) => sum + j.backlog, 0), 1)}
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} color="white" />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 2 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Budget</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Actual Cost</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Open POs</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>ETC Total</Text>
                                {/* <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Labor</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Equip</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Matl</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Rent</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Subs</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Other</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>Cont</Text> */}
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>EAC</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'center' }]}>% Complete</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Backlog</Text>
                            </View>
                            {totalBacklogDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No jobs with backlog data</Text>
                                </View>
                            ) : (
                                totalBacklogDetails.map((job, index) => (
                                    <View key={index} style={styles.billingRow}>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.jobId}</Text>
                                        <Text style={[styles.billingCell, { flex: 2 }]}>{job.description}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.budget, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.actualCost, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.openCommitments, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right', fontWeight: '600' }]}>{fNum(job.etc, 0)}</Text>
                                        {/* <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcLabor, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcEquipment, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcMaterials, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcRentals, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcSubcontractors, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcOther, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 0.7, textAlign: 'right', fontSize: 10 }]}>{fNum(job.etcContingency, 0)}</Text> */}
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.eac, 0)}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'center' }]}>{job.percentComplete.toFixed(0)}%</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right', fontWeight: '600' }]}>{fNum(job.backlog, 0)}</Text>
                                    </View>
                                ))
                            )}
                        </View>
                    </View>
                )}

                {/* Expanded Jobs Due for Billing */}
                {expandedMetric === 'jobsDueForBill' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                Jobs Due for a Bill ({jobsDueForBillingDetails.filter(j => j.isDue).length} Due / {jobsDueForBillingDetails.length} Total)
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 2 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Last Billed</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Billing Frequency</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Status</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Earned Revenue</Text>
                            </View>
                            {jobsDueForBillingDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No jobs with billing data</Text>
                                </View>
                            ) : (
                                jobsDueForBillingDetails.map((job, index) => (
                                    <View key={index} style={[
                                        styles.billingRow,
                                        job.isDue && { backgroundColor: '#FFF9E6' }
                                    ]}>
                                        <Text style={[styles.billingCell, { flex: 1, fontWeight: job.isDue ? '600' : '400' }]}>{job.jobId}</Text>
                                        <Text style={[styles.billingCell, { flex: 3 }]}>{job.description}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.lastBilledDate}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.billingFrequency}</Text>
                                        <Text style={[
                                            styles.billingCell,
                                            {
                                                flex: 1,
                                                color: job.isDue ? '#D97706' : '#666',
                                                fontWeight: job.isDue ? '600' : '400'
                                            }
                                        ]}>{job.dueStatus}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.earnedRevenue, 0)}</Text>
                                    </View>
                                ))
                            )}
                        </View>
                    </View>
                )}

                {/* Expanded Over/Under Billing */}
                {expandedMetric === 'overUnderBilling' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                Over / Under Billing Breakdown
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} color="white" />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 3 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Earned Revenue</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Billings</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Over/Under</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Status</Text>
                            </View>
                            {overUnderBillingDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No billing data available</Text>
                                </View>
                            ) : (
                                <>
                                    {overUnderBillingDetails.map((job, index) => (
                                        <View key={index} style={styles.billingRow}>
                                            <Text style={[styles.billingCell, { flex: 1 }]}>{job.jobId}</Text>
                                            <Text style={[styles.billingCell, { flex: 3 }]}>{job.description}</Text>
                                            <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.earnedRevenue, 0)}</Text>
                                            <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.billings, 0)}</Text>
                                            <Text style={[styles.billingCell, {
                                                flex: 1,
                                                textAlign: 'right',
                                                color: job.overUnder > 0 ? '#4CAF50' : job.overUnder < 0 ? '#F44336' : '#666',
                                                fontWeight: '600'
                                            }]}>{fNum(job.overUnder, 0)}</Text>
                                            <Text style={[styles.billingCell, { flex: 1 }]}>{job.status}</Text>
                                        </View>
                                    ))}
                                    <View style={[styles.billingRow, { backgroundColor: '#f8f9fa', borderTopWidth: 2, borderTopColor: '#333' }]}>
                                        <Text style={[styles.billingCell, { flex: 1, fontWeight: '700' }]}>TOTAL</Text>
                                        <Text style={[styles.billingCell, { flex: 3 }]}></Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right', fontWeight: '700' }]}>
                                            {fNum(overUnderBillingDetails.reduce((sum, j) => sum + j.earnedRevenue, 0), 0)}
                                        </Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right', fontWeight: '700' }]}>
                                            {fNum(overUnderBillingDetails.reduce((sum, j) => sum + j.billings, 0), 0)}
                                        </Text>
                                        <Text style={[styles.billingCell, {
                                            flex: 1,
                                            textAlign: 'right',
                                            fontWeight: '700',
                                            color: overUnderBillingDetails.reduce((sum, j) => sum + j.overUnder, 0) > 0 ? '#4CAF50' : '#F44336'
                                        }]}>
                                            {fNum(overUnderBillingDetails.reduce((sum, j) => sum + j.overUnder, 0), 0)}
                                        </Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}></Text>
                                    </View>
                                </>
                            )}
                        </View>
                    </View>
                )}

                {/* Expanded Active Projects */}
                {expandedMetric === 'activeProjects' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                Active & Pending Projects ({activeProjectsDetails.filter(j => j.status === 'Active').length} Active / {activeProjectsDetails.filter(j => j.status === 'Pending').length} Pending)
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} color="white" />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 3 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Status</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Start Date</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1, textAlign: 'right' }]}>Budget</Text>
                            </View>
                            {activeProjectsDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No active or pending projects</Text>
                                </View>
                            ) : (
                                activeProjectsDetails.map((job, index) => (
                                    <View key={index} style={[
                                        styles.billingRow,
                                        job.status === 'Active' && { backgroundColor: '#E8F5E9' }
                                    ]}>
                                        <Text style={[styles.billingCell, { flex: 1, fontWeight: job.status === 'Active' ? '600' : '400' }]}>{job.jobId}</Text>
                                        <Text style={[styles.billingCell, { flex: 3 }]}>{job.description}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, color: job.status === 'Active' ? '#4CAF50' : '#FF9800', fontWeight: '600' }]}>{job.status}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.startDate}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, textAlign: 'right' }]}>{fNum(job.budget, 0)}</Text>
                                    </View>
                                ))
                            )}
                        </View>
                    </View>
                )}

                {/* Expanded Jobs Finishing */}
                {expandedMetric === 'jobsFinishing' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                Jobs Finishing in Next 30 Days ({jobsFinishingDetails.length})
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} color="white" />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 3 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Complete Date</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Days Until Complete</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Status</Text>
                            </View>
                            {jobsFinishingDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No jobs finishing in the next 30 days</Text>
                                </View>
                            ) : (
                                jobsFinishingDetails.map((job, index) => (
                                    <View key={index} style={styles.billingRow}>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.jobId}</Text>
                                        <Text style={[styles.billingCell, { flex: 3 }]}>{job.description}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.completeDate}</Text>
                                        <Text style={[styles.billingCell, { flex: 1, color: job.daysUntilComplete <= 7 ? '#F44336' : '#666' }]}>{job.daysUntilComplete} days</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.status}</Text>
                                    </View>
                                ))
                            )}
                        </View>
                    </View>
                )}

                {/* Expanded Jobs Starting */}
                {expandedMetric === 'jobsStarting' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                Jobs Starting in Next 4 Weeks ({jobsStartingDetails.length})
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} color="white" />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 3 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Start Date</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Days Until Start</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Status</Text>
                            </View>
                            {jobsStartingDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No jobs starting in the next 4 weeks</Text>
                                </View>
                            ) : (
                                jobsStartingDetails.map((job, index) => (
                                    <View key={index} style={styles.billingRow}>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.jobId}</Text>
                                        <Text style={[styles.billingCell, { flex: 3 }]}>{job.description}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.startDate}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.daysUntilStart} days</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.status}</Text>
                                    </View>
                                ))
                            )}
                        </View>
                    </View>
                )}

                {/* Expanded New Jobs */}
                {expandedMetric === 'newJobs' && (
                    <View style={styles.expandedSection}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <Text style={styles.expandedTitle}>
                                New Jobs (Last 30 Days) - {newJobsDetails.length} Jobs
                            </Text>
                            <Pressable
                                style={styles.closeButton}
                                onPress={() => setExpandedMetric(null)}
                            >
                                <Icon3 icon={MdClose} size={20} color="white" />
                            </Pressable>
                        </View>
                        <View style={styles.billingTable}>
                            <View style={styles.billingHeader}>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Job ID</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 3 }]}>Description</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Created Date</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Days Old</Text>
                                <Text style={[styles.billingHeaderCell, { flex: 1 }]}>Status</Text>
                            </View>
                            {newJobsDetails.length === 0 ? (
                                <View style={[styles.billingRow, { justifyContent: 'center', paddingVertical: 20 }]}>
                                    <Text style={[styles.billingCell, { textAlign: 'center', color: '#999' }]}>No new jobs in the last 30 days</Text>
                                </View>
                            ) : (
                                newJobsDetails.map((job, index) => (
                                    <View key={index} style={styles.billingRow}>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.jobId}</Text>
                                        <Text style={[styles.billingCell, { flex: 3 }]}>{job.description}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.createdDate}</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.daysOld} days</Text>
                                        <Text style={[styles.billingCell, { flex: 1 }]}>{job.status}</Text>
                                    </View>
                                ))
                            )}
                        </View>
                    </View>
                )}

                {/* Charts Section */}
                <View style={styles.chartsSection}>
                    <View style={styles.chartCard}>
                        <Text style={styles.chartTitle}>Total Labor Hours Trend (Actual vs Forecast)</Text>
                        {isLoading ? (
                            <View style={{ padding: 20 }}>
                                <SkeletonBox width="100%" height={200} />
                            </View>
                        ) : (
                        <Line
                            data={chartData.laborHoursTrend}
                            options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'top',
                                        align: 'end',
                                        labels: {
                                            usePointStyle: false,
                                            padding: 10,
                                            font: {
                                                size: 11
                                            },
                                            color: '#666',
                                            boxWidth: 40,
                                            boxHeight: 2
                                        }
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            precision: 0,
                                            font: {
                                                size: 12
                                            }
                                        },
                                        title: {
                                            display: true,
                                            text: 'Hours (in thousands)',
                                            font: {
                                                size: 13,
                                                weight: 'bold'
                                            }
                                        }
                                    },
                                    x: {
                                        ticks: {
                                            font: {
                                                size: 12
                                            }
                                        }
                                    }
                                }
                            }}
                            height={150}
                            width={350}
                        />
                        )}
                    </View>

                    <View style={styles.chartCard}>
                        <Text style={styles.chartTitle}>Jobs Finishing (Next 30 Days)</Text>
                        {isLoading ? (
                            <View style={{ padding: 20 }}>
                                <SkeletonBox width="100%" height={200} />
                            </View>
                        ) : (
                        <>
                        <Text style={{ fontSize: 36, fontWeight: 'bold', textAlign: 'center', marginBottom: 15 }}>
                            {chartData.jobsFinishingChart?.totalJobs || 0}
                        </Text>
                        <Bar
                            data={chartData.jobsFinishingChart}
                            options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: {
                                    legend: {
                                        display: false
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            precision: 0,
                                            font: {
                                                size: 12
                                            }
                                        }
                                    },
                                    x: {
                                        ticks: {
                                            font: {
                                                size: 11
                                            }
                                        }
                                    }
                                }
                            }}
                            height={100}
                            width={250}
                        />
                        </>
                        )}
                    </View>
                </View>

                {/* Contingency Metrics */}
                {/* <View style={styles.contingencySection}>
                    <View style={styles.contingencyGrid}>
                        {contingencyMetrics.map((metric, index) => (
                            <View key={index} style={styles.contingencyCard}>
                                <Text style={styles.contingencyLabel}>{metric.label}</Text>
                                <Text style={styles.contingencyValue}>{metric.value}</Text>
                            </View>
                        ))}
                    </View>
                </View> */}

                {/* Job-Level KPIs */}
                <View style={styles.kpiSection}>
                    <div className="ag-theme-alpine" style={{ width: '100%' }}>
                        <AgGridReact
                            rowData={jobKPIs}
                            columnDefs={[
                                { 
                                    field: 'jobName', 
                                    headerName: 'Job Name', 
                                    flex: 2,
                                    cellStyle: { textAlign: 'left' }
                                },
                                { 
                                    field: 'eac', 
                                    headerName: 'EAC $', 
                                    flex: 1,
                                    valueFormatter: params => params.value?.toLocaleString() || '0',
                                    cellStyle: { textAlign: 'right' }
                                },
                                { 
                                    field: 'gm', 
                                    headerName: 'GM %', 
                                    flex: 1,
                                    valueFormatter: params => `${(params.value || 0).toFixed(0)}%`,
                                    cellStyle: params => ({
                                        textAlign: 'right',
                                        color: (params.value || 0) >= 0 ? '#4CAF50' : '#F44336'
                                    })
                                },
                                { 
                                    field: 'completePercent', 
                                    headerName: 'Complete %', 
                                    flex: 1,
                                    valueFormatter: params => `${(params.value || 0).toFixed(0)}%`,
                                    cellStyle: { textAlign: 'right' }
                                },
                                { 
                                    field: 'overUnder', 
                                    headerName: 'Over/Under Billing', 
                                    flex: 1,
                                    valueFormatter: params => params.value?.toLocaleString() || '0',
                                    cellStyle: params => ({
                                        textAlign: 'right',
                                        color: params.value > 0 ? '#4CAF50' : params.value < 0 ? '#F44336' : '#666',
                                        fontWeight: '600'
                                    })
                                },
                                { 
                                    field: 'startDate', 
                                    headerName: 'Start Date', 
                                    flex: 1,
                                    cellStyle: { textAlign: 'right' }
                                },
                                { 
                                    field: 'endDate', 
                                    headerName: 'End Date', 
                                    flex: 1,
                                    cellStyle: { textAlign: 'right' }
                                }
                            ]}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true
                            }}
                            domLayout='autoHeight'
                        />
                    </div>
                </View>
            </ScrollView>
            )}
        </View>
    );
};

export default Dashboard;


